import { type AxiosRequestConfig } from 'axios'
import { add as addToDate, isPast } from 'date-fns'
import Cookies from 'js-cookie'
import { v4 as uuid } from 'uuid'

import {
  ClidData,
  ClidDataMapper,
  CrmDataMapper,
  EmailData,
  EmailDataMapper,
  GclidData,
  GclidDataMapper,
  VisitorTypeDataMapper,
  ZaidData,
  ZaidDataMapper,
} from './tracking/expirableData'
import { canUseDom, canUseWindow } from './utils/canUse'
import { APP_VERSION_VALUE, BFF_KEY_VALUE } from './build'
import { VisitorType } from './enums'
import { type UserAccountFound } from './generated'
import { type AugmentedAxiosRequestConfig } from './interceptors.types'
import {
  ATTRIBUTION_CLID_STORAGE,
  ATTRIBUTION_CRM_STORAGE,
  ATTRIBUTION_GCLID_STORAGE,
  CONSENT_ID_STORAGE,
  CONSENT_STRING_STORAGE,
  CONSENT_VENDORS_S2S_STORAGE,
  DEFAULT_THEME_STORAGE,
  EMAIL_HIDDEN_STORAGE,
  EMAIL_STRONG_STORAGE,
  EMAIL_STRONGER_STORAGE,
  EVENTS_CLICK_STORAGE,
  EVENTS_PUSH_STORAGE,
  EXEMPT_ID_STORAGE,
  NAVIGATION_PREVIOUS_STORAGE,
  SEARCH_USAGE_STORAGE,
  StorageSdk,
  THEME_STORAGE,
  TRACE_ID_STORAGE,
  VISITOR_TYPE_STORAGE,
  ZEMANTA_ID_STORAGE,
} from './storage'
import {
  APP_VERSION_HEADER,
  ATTRIBUTION_CAMPAIGN_HEADER,
  ATTRIBUTION_CLID_HEADER,
  ATTRIBUTION_CONTENT_HEADER,
  ATTRIBUTION_CRM_HEADER,
  ATTRIBUTION_EFFI_ID_HEADER,
  ATTRIBUTION_EFFI_ID2_HEADER,
  ATTRIBUTION_GCLID_HEADER,
  ATTRIBUTION_ID_COMPTEUR_HEADER,
  ATTRIBUTION_MEDIUM_HEADER,
  ATTRIBUTION_PREX_HEADER,
  ATTRIBUTION_REFERRER_HEADER,
  ATTRIBUTION_SOURCE_HEADER,
  ATTRIBUTION_TERM_HEADER,
  BFF_KEY_HEADER,
  CHANNEL_HEADER,
  CLIENT_APP_ID_HEADER,
  CONSENT_EXEMPT_ID_HEADER,
  CONSENT_ID_HEADER,
  CONSENT_STRING_HEADER,
  CONSENT_VENDORS_HEADER,
  DEVICE_CLASS_HEADER,
  DEVICE_OS_VERSION_HEADER,
  EFFI_ID,
  EFFI_ID2,
  EMAIL_HIDDEN_HEADER,
  EMAIL_STRONG_HEADER,
  EMAIL_STRONGER_HEADER,
  ENV_HEADER,
  GCLID,
  ID_COMPTEUR,
  LOCALE_HEADER,
  NAVIGATION_CURRENT_HEADER,
  NAVIGATION_PAGE_PARAMS,
  NAVIGATION_PREVIOUS_HEADER,
  POST_BACK_ID,
  PREX,
  SEARCH_USAGE_HEADER,
  TRACE_ID_HEADER,
  USER_DEVICE_THEME_HEADER,
  UTM_CAMPAIGN,
  UTM_CONTENT,
  UTM_MEDIUM,
  UTM_SOURCE,
  UTM_TERM,
  VIRTUAL_ENV_NAME,
  VISITOR_TYPE_HEADER,
  ZEMANTA_ID,
  ZEMANTA_ID_HEADER,
} from './userConstants'

// COOKIE KEYS
export const VISITOR_ID_COOKIE = 'x-visitor-id'

// On souhaite renouveler le token 10 minutes avant l'expiration réelle.
const EXPIRATION_LEEWAY = 60 * 2 // in seconds

export const attributionParamsToTrack: string[] = [
  UTM_SOURCE,
  UTM_MEDIUM,
  UTM_CAMPAIGN,
  UTM_TERM,
  UTM_CONTENT,
  GCLID,
  POST_BACK_ID,
]

export class DeviceSettings {
  readonly locale: string

  readonly channel: string

  readonly deviceId: string

  readonly env: string

  readonly osVersion?: string

  readonly deviceClass?: string

  readonly virtualEnvName?: string

  constructor(
    locale: string,
    channel: string,
    deviceId: string,
    env: string,
    osVersion?: string,
    deviceClass?: string,
    virtualEnvName?: string,
  ) {
    this.locale = locale
    this.channel = channel
    this.deviceId = deviceId
    this.env = env
    this.osVersion = osVersion
    this.deviceClass = deviceClass
    this.virtualEnvName = virtualEnvName
  }
}

export class User {
  deviceSettings: DeviceSettings

  constructor(deviceSettings: DeviceSettings) {
    this.deviceSettings = deviceSettings
  }
}

type RefreshTokenDataEvent =
  | {
      type: 'REFRESH_TOKEN_DATA'
      payload: UserAccountFound
    }
  | {
      type: 'REFRESH_TOKEN_DATA_ERROR'
      payload: unknown
    }

export type RefreshTokenDataEventListener = (event: RefreshTokenDataEvent) => void

class RefreshTokenDataEventEmitter {
  #listeners: RefreshTokenDataEventListener[] = []

  subscribe(listenerToAdd: RefreshTokenDataEventListener): void {
    this.#listeners.push(listenerToAdd)
  }

  unsubscribe(listenerToRemove: RefreshTokenDataEventListener): void {
    this.#listeners = this.#listeners.filter((listener) => listener !== listenerToRemove)
  }

  dispatch(event: RefreshTokenDataEvent): void {
    this.#listeners.forEach((listener) => listener(event))
  }
}

export class UserSdk {
  readonly user: User

  readonly #refreshTokenDataEventEmitter = new RefreshTokenDataEventEmitter()

  readonly storageSdk: StorageSdk

  #expiresAt?: Date

  constructor(
    user: User,
    refreshTokenDataEventEmitter = new RefreshTokenDataEventEmitter(),
    storageSdk: StorageSdk = new StorageSdk(),
  ) {
    this.user = user
    this.#refreshTokenDataEventEmitter = refreshTokenDataEventEmitter
    this.storageSdk = storageSdk
  }

  copyWithUser(user: User): UserSdk {
    const copy = new UserSdk(user, this.#refreshTokenDataEventEmitter, this.storageSdk)
    copy.#expiresAt = this.#expiresAt

    return copy
  }

  subscribeRefreshTokenDataEventListener(listenerToAdd: RefreshTokenDataEventListener): void {
    this.#refreshTokenDataEventEmitter.subscribe(listenerToAdd)
  }

  unsubscribeRefreshTokenDataEventListener = (listenerToRemove: RefreshTokenDataEventListener): void => {
    this.#refreshTokenDataEventEmitter.unsubscribe(listenerToRemove)
  }

  notifyRefreshTokenData = (data: UserAccountFound): void => {
    this.updateTokenData(data)
    this.#refreshTokenDataEventEmitter.dispatch({
      type: 'REFRESH_TOKEN_DATA',
      payload: data,
    })
  }

  notifyRefreshTokenError = (error: unknown): void => {
    this.resetTokenData()
    this.#refreshTokenDataEventEmitter.dispatch({
      type: 'REFRESH_TOKEN_DATA_ERROR',
      payload: error,
    })
  }

  isRefreshTokensNeeded = (): boolean => Boolean(this.#expiresAt && isPast(this.#expiresAt))

  setInvalidTokenData = (): void => {
    this.#expiresAt = new Date(0)
  }

  resetTokenData = (): void => {
    this.#expiresAt = undefined
  }

  updateTokenData = (data: UserAccountFound): void => {
    this.#expiresAt = addToDate(new Date(), { seconds: data.expireIn - EXPIRATION_LEEWAY })
  }

  updateDeviceSettings = (settings: DeviceSettings): User => {
    this.user.deviceSettings = settings

    return this.user
  }

  updateUserConsentData = (
    userConsentedVendors: string,
    userConsentString: string,
    consentId?: string,
    exemptId?: string,
  ): User => {
    this.storageSdk.localStorage()?.setItem(CONSENT_VENDORS_S2S_STORAGE, userConsentedVendors)
    this.storageSdk.localStorage()?.setItem(CONSENT_STRING_STORAGE, userConsentString)

    if (consentId) {
      this.storageSdk.localStorage()?.setItem(CONSENT_ID_STORAGE, consentId)
    }

    if (exemptId) {
      this.storageSdk.localStorage()?.setItem(EXEMPT_ID_STORAGE, exemptId)
    }

    return this.user
  }

  getTraceId(): string {
    return this.storageSdk.localStorage()?.getItem(TRACE_ID_STORAGE) || ''
  }

  createVisitorId(): string {
    const PREFIX_VISITOR_ID_VALUE = 'fba'
    const uuidGenerated: string = uuid()
      .replace(/[^A-Za-z0-9\s]/g, '')
      .substring(0, 97) // due to adobe limit (100 characters)

    return `${PREFIX_VISITOR_ID_VALUE}${uuidGenerated}`
  }

  getOrInitVisitorId(): string | undefined {
    if (canUseDom()) {
      const cookieVisitorId = Cookies.get(VISITOR_ID_COOKIE)

      if (cookieVisitorId) {
        return cookieVisitorId
      }
      const newVisitorId = this.createVisitorId()
      const expiryDate = new Date()
      expiryDate.setFullYear(expiryDate.getFullYear() + 1)
      Cookies.set(VISITOR_ID_COOKIE, newVisitorId, { secure: true, path: '/', expires: expiryDate, sameSite: 'Lax' })

      return newVisitorId
    }

    return undefined
  }

  getConsentVendors(): string {
    return this.storageSdk.localStorage()?.getItem(CONSENT_VENDORS_S2S_STORAGE) || ''
  }

  getConsentString(): string {
    return this.storageSdk.localStorage()?.getItem(CONSENT_STRING_STORAGE) || ''
  }

  getConsentId(): string {
    return this.storageSdk.localStorage()?.getItem(CONSENT_ID_STORAGE) || ''
  }

  getExemptId(): string {
    return this.storageSdk.localStorage()?.getItem(EXEMPT_ID_STORAGE) || ''
  }

  getVisitorType(): string {
    return this.storageSdk.localStorage()?.getItem(VISITOR_TYPE_STORAGE) || VisitorType.NEW_USER
  }

  getEmailHidden(): string | undefined {
    const emailHiddenRaw = this.storageSdk.localStorage()?.getItem(EMAIL_HIDDEN_STORAGE)

    if (emailHiddenRaw) {
      try {
        const emailHidden = EmailDataMapper.fromJSON(emailHiddenRaw)

        if (emailHidden.isExpired()) {
          this.storageSdk.localStorage()?.removeItem(EMAIL_HIDDEN_STORAGE)
        } else {
          return emailHidden.email
        }
      } catch {
        this.storageSdk.localStorage()?.removeItem(EMAIL_HIDDEN_STORAGE)
      }
    }

    return undefined
  }

  setEmailHidden(emailHidden: string): void {
    this.storageSdk.localStorage()?.setItem(EMAIL_HIDDEN_STORAGE, EmailDataMapper.toJSON(new EmailData(emailHidden)))
  }

  getEmailStrong(): string | undefined {
    const emailStrongRaw = this.storageSdk.localStorage()?.getItem(EMAIL_STRONG_STORAGE)

    if (emailStrongRaw) {
      try {
        const emailStrong = EmailDataMapper.fromJSON(emailStrongRaw)

        if (emailStrong.isExpired()) {
          this.storageSdk.localStorage()?.removeItem(EMAIL_STRONG_STORAGE)
        } else {
          return emailStrong.email
        }
      } catch {
        this.storageSdk.localStorage()?.removeItem(EMAIL_STRONG_STORAGE)
      }
    }

    return undefined
  }

  setEmailStrong(emailStrong: string): void {
    this.storageSdk.localStorage()?.setItem(EMAIL_STRONG_STORAGE, EmailDataMapper.toJSON(new EmailData(emailStrong)))
  }

  getEmailStronger(): string | undefined {
    const emailStrongerRaw = this.storageSdk.localStorage()?.getItem(EMAIL_STRONGER_STORAGE)

    if (emailStrongerRaw) {
      try {
        const emailStronger = EmailDataMapper.fromJSON(emailStrongerRaw)

        if (emailStronger.isExpired()) {
          this.storageSdk.localStorage()?.removeItem(EMAIL_STRONGER_STORAGE)
        } else {
          return emailStronger.email
        }
      } catch {
        this.storageSdk.localStorage()?.removeItem(EMAIL_STRONGER_STORAGE)
      }
    }

    return undefined
  }

  setEmailStronger(emailStronger: string): void {
    this.storageSdk
      .localStorage()
      ?.setItem(EMAIL_STRONGER_STORAGE, EmailDataMapper.toJSON(new EmailData(emailStronger)))
  }

  getPreviousDatas(): string {
    const previousDatas = this.storageSdk.sessionStorage()?.getItem(NAVIGATION_PREVIOUS_STORAGE)

    if (previousDatas) {
      const { previousPageName, clickPosition } = JSON.parse(previousDatas)

      return clickPosition ? `${previousPageName}:${clickPosition}` : previousPageName
    }

    return ''
  }

  getDeferredEventsPush(): Set<string> {
    const eventsString = this.storageSdk.sessionStorage()?.getItem(EVENTS_PUSH_STORAGE)

    return new Set<string>(eventsString ? JSON.parse(eventsString) : new Set<string>())
  }

  getDeferredEventsClick(): Set<string> {
    const eventsString = this.storageSdk.sessionStorage()?.getItem(EVENTS_CLICK_STORAGE)

    return new Set<string>(eventsString ? JSON.parse(eventsString) : new Set<string>())
  }

  getSearchUsage(): string {
    return this.storageSdk.sessionStorage()?.getItem(SEARCH_USAGE_STORAGE) || ''
  }

  getTheme(): string {
    return this.storageSdk.localStorage()?.getItem(THEME_STORAGE) || DEFAULT_THEME_STORAGE
  }

  getHeaders = (): Record<string, string> => {
    this.getOrInitVisitorId()
    const headers: Record<string, string> = {}
    headers[CHANNEL_HEADER] = this.user.deviceSettings?.channel as string
    headers[CLIENT_APP_ID_HEADER] = this.user.deviceSettings?.deviceId as string
    headers[VIRTUAL_ENV_NAME] = this.user.deviceSettings?.virtualEnvName as string
    headers[ENV_HEADER] = this.user.deviceSettings?.env as string
    headers[LOCALE_HEADER] = this.user.deviceSettings?.locale as string
    headers[EMAIL_HIDDEN_HEADER] = this.getEmailHidden() as string
    headers[EMAIL_STRONG_HEADER] = this.getEmailStrong() as string
    headers[EMAIL_STRONGER_HEADER] = this.getEmailStronger() as string
    headers[CONSENT_VENDORS_HEADER] = this.getConsentVendors()
    headers[CONSENT_STRING_HEADER] = this.getConsentString()
    headers[CONSENT_ID_HEADER] = this.getConsentId()
    headers[CONSENT_EXEMPT_ID_HEADER] = this.getExemptId()
    headers[APP_VERSION_HEADER] = APP_VERSION_VALUE
    headers[BFF_KEY_HEADER] = BFF_KEY_VALUE
    headers[DEVICE_OS_VERSION_HEADER] = this.user.deviceSettings?.osVersion as string
    headers[DEVICE_CLASS_HEADER] = this.user.deviceSettings?.deviceClass as string
    headers[ATTRIBUTION_MEDIUM_HEADER] = this.getParamFromUrl(UTM_MEDIUM)
    headers[ATTRIBUTION_SOURCE_HEADER] = this.getParamFromUrl(UTM_SOURCE)
    headers[ATTRIBUTION_CAMPAIGN_HEADER] = this.getParamFromUrl(UTM_CAMPAIGN)
    headers[ATTRIBUTION_TERM_HEADER] = this.getParamFromUrl(UTM_TERM)
    headers[ATTRIBUTION_CONTENT_HEADER] = this.getParamFromUrl(UTM_CONTENT)
    headers[ATTRIBUTION_PREX_HEADER] = this.getParamFromUrl(PREX)
    headers[ATTRIBUTION_ID_COMPTEUR_HEADER] = this.getParamFromUrl(ID_COMPTEUR)
    headers[ATTRIBUTION_EFFI_ID_HEADER] = this.getParamFromUrl(EFFI_ID)
    headers[ATTRIBUTION_EFFI_ID2_HEADER] = this.getParamFromUrl(EFFI_ID2)
    headers[ATTRIBUTION_CRM_HEADER] = this.getCrm()
    headers[ATTRIBUTION_CLID_HEADER] = this.getClid()
    headers[ATTRIBUTION_GCLID_HEADER] = this.getGclid()
    headers[ATTRIBUTION_REFERRER_HEADER] = canUseDom() ? document.referrer : ''
    headers[NAVIGATION_PREVIOUS_HEADER] = this.getPreviousDatas()
    headers[NAVIGATION_CURRENT_HEADER] = canUseWindow() ? window.location.pathname : ''
    headers[VISITOR_TYPE_HEADER] = VisitorTypeDataMapper.fromJSON(this.getVisitorType()).visitorType
    headers[NAVIGATION_PAGE_PARAMS] = this.getAttributionPageParams()
    headers[TRACE_ID_HEADER] = this.getTraceId()
    headers[ZEMANTA_ID_HEADER] = this.getZemantaId()
    headers[SEARCH_USAGE_HEADER] = this.getSearchUsage()
    headers[USER_DEVICE_THEME_HEADER] = this.getTheme()
    Object.keys(headers).forEach((key) =>
      headers[key] === null || headers[key] === undefined || headers[key] === '' ? delete headers[key] : {},
    )

    return headers
  }

  getAttributionPageParams = (): string =>
    canUseWindow() && this.isParamsTrackable(window.location.search) ? encodeURI(window.location.search) : ''

  getParamFromUrl(param: string): string {
    const paramValue = canUseWindow() ? new URLSearchParams(encodeURI(window.location.search)).get(param) : null

    return paramValue ? encodeURIComponent(paramValue) : ''
  }

  // return PostBackId if present in URL
  getClid(): string {
    // Deleting older Clid if existing (previous campaign) but new campaign has been launched
    if (canUseWindow() && this.isParamsTrackable(window.location.search)) {
      this.storageSdk.localStorage()?.removeItem(ATTRIBUTION_CLID_STORAGE)
    }
    const postBackId = canUseWindow() ? new URLSearchParams(window.location.search).get(POST_BACK_ID) : null

    if (postBackId) {
      this.storageSdk
        .localStorage()
        ?.setItem(
          ATTRIBUTION_CLID_STORAGE,
          ClidDataMapper.toJSON(new ClidData(new Date(), postBackId).resetExpirationDate()),
        )
    }

    return (
      encodeURIComponent(
        ClidDataMapper.fromJSON(this.storageSdk.localStorage()?.getItem(ATTRIBUTION_CLID_STORAGE)).clid,
      ) || ''
    )
  }

  getGclid(): string {
    // Deleting older GCLID if existing (previous campaign) but new campaign has been launched
    if (canUseWindow() && this.isParamsTrackable(window.location.search)) {
      this.storageSdk.localStorage()?.removeItem(ATTRIBUTION_GCLID_STORAGE)
    }
    const paramValue = canUseWindow() ? new URLSearchParams(window.location.search).get(GCLID) : null

    if (paramValue) {
      this.storageSdk
        .localStorage()
        ?.setItem(
          ATTRIBUTION_GCLID_STORAGE,
          GclidDataMapper.toJSON(new GclidData(new Date(), paramValue).resetExpirationDate()),
        )
    }

    return (
      encodeURIComponent(
        GclidDataMapper.fromJSON(this.storageSdk.localStorage()?.getItem(ATTRIBUTION_GCLID_STORAGE)).gclid,
      ) || ''
    )
  }

  getZemantaId(): string {
    const paramValue = canUseWindow() ? new URLSearchParams(window.location.search).get(ZEMANTA_ID) : null

    if (paramValue) {
      this.storageSdk
        .localStorage()
        ?.setItem(ZEMANTA_ID_STORAGE, ZaidDataMapper.toJSON(new ZaidData(new Date(), paramValue).resetExpirationDate()))
    }

    return (
      encodeURIComponent(ZaidDataMapper.fromJSON(this.storageSdk.localStorage()?.getItem(ZEMANTA_ID_STORAGE)).zaid) ||
      ''
    )
  }

  getCrm(): string {
    return (
      encodeURIComponent(
        CrmDataMapper.fromJSON(this.storageSdk.localStorage()?.getItem(ATTRIBUTION_CRM_STORAGE)).crm,
      ) || ''
    )
  }

  getBffHeader = (): string => this.getHeaders()[BFF_KEY_HEADER]

  createAxiosOptions = (shouldRefreshTokens = true, options?: AxiosRequestConfig): AugmentedAxiosRequestConfig => ({
    headers: { ...this.getHeaders(), ...options?.headers },
    ...(options?.params && { params: { ...options?.params } }),
    // Namespace to avoid potential conflicts with official Axios options
    sibr: {
      shouldRefreshTokens,
    },
  })

  private isParamsTrackable = (params: string): boolean => attributionParamsToTrack.some((ele) => params.includes(ele))
}
